import React, { useState } from 'react';
import { Box, Typography, Chip, Button, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import { ArrowBack, CheckCircle, AccessTime, EmojiEvents } from '@mui/icons-material';
import { Exercice } from '../../types';
import { latexToSimpleText } from '../../utils/latexToText';

interface ExerciceMobileViewProps {
  exercice: Omit<Exercice, 'id'>;
  chapitreTitre?: string;
  leconTitre?: string;
}

/**
 * Composant qui reproduit l'affichage mobile d'un exercice
 * Basé sur le design de l'application mobile Suklu Academy
 */
const ExerciceMobileView: React.FC<ExerciceMobileViewProps> = ({
  exercice,
  chapitreTitre,
  leconTitre,
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{ [key: number]: string }>({});

  const currentQuestion = exercice.questions[currentQuestionIndex];
  const totalQuestions = exercice.questions.length;
  const totalPoints = exercice.questions.reduce((sum, q) => sum + q.points, 0);

  const handleAnswerSelect = (answer: string) => {
    setSelectedAnswers({
      ...selectedAnswers,
      [currentQuestionIndex]: answer,
    });
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < totalQuestions - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  return (
    <Box
      sx={{
        height: '667px', // Hauteur iPhone SE / 8 (standard)
        width: '100%',
        maxWidth: '375px', // Largeur iPhone SE / 8
        bgcolor: '#f5f5f5',
        overflow: 'auto', // Scroll interne
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #ddd',
        borderRadius: '12px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          p: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <ArrowBack sx={{ fontSize: 20, color: '#666' }} />
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            fontSize: '16px',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {exercice.titre || 'Exercice'}
        </Typography>
      </Box>

      {/* Informations de l'exercice */}
      <Box sx={{ p: 2 }}>
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '12px',
            p: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            mb: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontSize: '18px',
              fontWeight: 700,
              mb: 1,
              color: '#1a1a1a',
            }}
          >
            {exercice.titre}
          </Typography>

          {/* Badges */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <Chip
              label={chapitreTitre || 'Chapitre'}
              size="small"
              sx={{
                bgcolor: '#2196f3',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px',
                height: '24px',
              }}
            />
            <Chip
              label={exercice.niveau || 'moyen'}
              size="small"
              sx={{
                bgcolor:
                  exercice.niveau === 'facile'
                    ? '#4caf50'
                    : exercice.niveau === 'difficile'
                    ? '#f44336'
                    : '#ff9800',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px',
                height: '24px',
              }}
            />
          </Box>

          {/* Statistiques */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <CheckCircle sx={{ fontSize: 16, color: '#4caf50' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                {totalQuestions} questions
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <EmojiEvents sx={{ fontSize: 16, color: '#ffc107' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                {totalPoints} points
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AccessTime sx={{ fontSize: 16, color: '#666' }} />
              <Typography variant="caption" sx={{ color: '#666', fontSize: '13px' }}>
                {exercice.tempsEstimeMinutes || 30} min
              </Typography>
            </Box>
          </Box>

          {/* Description - convertir LaTeX en texte simple */}
          <Typography
            variant="body2"
            sx={{
              fontSize: '14px',
              color: '#666',
              lineHeight: 1.6,
              whiteSpace: 'pre-wrap',
            }}
          >
            {latexToSimpleText(exercice.description)}
          </Typography>
        </Box>

        {/* Progression des questions */}
        <Box
          sx={{
            bgcolor: 'white',
            borderRadius: '12px',
            p: 2,
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px' }}>
              Question {currentQuestionIndex + 1}/{totalQuestions}
            </Typography>
            <Chip
              label={`${currentQuestion?.points || 1} point${currentQuestion?.points > 1 ? 's' : ''}`}
              size="small"
              sx={{
                bgcolor: '#ffc107',
                color: 'white',
                fontWeight: 600,
                fontSize: '12px',
              }}
            />
          </Box>

          {/* Barre de progression */}
          <Box
            sx={{
              height: '6px',
              bgcolor: '#e0e0e0',
              borderRadius: '3px',
              overflow: 'hidden',
              mb: 2,
            }}
          >
            <Box
              sx={{
                width: `${((currentQuestionIndex + 1) / totalQuestions) * 100}%`,
                height: '100%',
                bgcolor: '#4caf50',
                borderRadius: '3px',
                transition: 'width 0.3s ease',
              }}
            />
          </Box>

          {/* Question actuelle */}
          {currentQuestion && (
            <Box>
              {/* Type de question */}
              <Chip
                label={
                  currentQuestion.type === 'choix_multiple'
                    ? 'Choix multiple'
                    : currentQuestion.type === 'vrai_faux'
                    ? 'Vrai/Faux'
                    : 'Texte libre'
                }
                size="small"
                variant="outlined"
                sx={{
                  mb: 2,
                  fontSize: '11px',
                  height: '22px',
                }}
              />

              {/* Texte de la question - convertir LaTeX en texte simple */}
              <Typography
                variant="body1"
                sx={{
                  fontSize: '16px',
                  fontWeight: 600,
                  color: '#1a1a1a',
                  mb: 3,
                  lineHeight: 1.6,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {latexToSimpleText(currentQuestion.texte)}
              </Typography>

              {/* Illustration LaTeX (si présente) - Afficher sous forme d'image si lienLatex existe */}
              {(currentQuestion.illustrationLatex || currentQuestion.lienLatex) && (
                <Box
                  sx={{
                    bgcolor: '#f9f9f9',
                    borderRadius: '8px',
                    p: 2,
                    mb: 3,
                    border: '1px solid #e0e0e0',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#666',
                      fontSize: '11px',
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    Illustration
                  </Typography>
                  {currentQuestion.lienLatex ? (
                    <Box
                      component="img"
                      src={currentQuestion.lienLatex}
                      alt="Illustration"
                      sx={{
                        width: '100%',
                        maxWidth: '100%',
                        height: 'auto',
                        borderRadius: '4px',
                        bgcolor: '#fff',
                        p: 1,
                        border: '1px solid #e0e0e0',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        bgcolor: '#fff',
                        p: 1.5,
                        borderRadius: '4px',
                        border: '1px dashed #ccc',
                        minHeight: '80px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#999',
                          fontSize: '12px',
                          textAlign: 'center',
                        }}
                      >
                        [Figure géométrique générée]
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Options de réponse */}
              {currentQuestion.type === 'choix_multiple' && currentQuestion.options && (
                <RadioGroup
                  value={selectedAnswers[currentQuestionIndex] || ''}
                  onChange={(e) => handleAnswerSelect(e.target.value)}
                >
                  {currentQuestion.options.map((option, index) => {
                    const optionLetter = String.fromCharCode(65 + index);
                    const isSelected = selectedAnswers[currentQuestionIndex] === optionLetter;

                    return (
                      <Box
                        key={index}
                        sx={{
                          bgcolor: isSelected ? '#e3f2fd' : 'white',
                          border: `2px solid ${isSelected ? '#2196f3' : '#e0e0e0'}`,
                          borderRadius: '12px',
                          p: 2,
                          mb: 1.5,
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            borderColor: '#2196f3',
                            bgcolor: '#f5f5f5',
                          },
                        }}
                        onClick={() => handleAnswerSelect(optionLetter)}
                      >
                        <FormControlLabel
                          value={optionLetter}
                          control={
                            <Radio
                              sx={{
                                color: '#ccc',
                                '&.Mui-checked': {
                                  color: '#2196f3',
                                },
                              }}
                            />
                          }
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '14px',
                                  color: isSelected ? '#2196f3' : '#666',
                                  minWidth: '24px',
                                }}
                              >
                                {optionLetter}.
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: '14px',
                                  color: isSelected ? '#1a1a1a' : '#666',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {latexToSimpleText(option || `Option ${optionLetter}`)}
                              </Typography>
                            </Box>
                          }
                          sx={{ margin: 0, width: '100%' }}
                        />
                      </Box>
                    );
                  })}
                </RadioGroup>
              )}

              {/* Explication (cachée jusqu'à validation) */}
              {currentQuestion.explication && false && (
                <Box
                  sx={{
                    bgcolor: '#e8f5e9',
                    borderRadius: '8px',
                    p: 2,
                    mt: 3,
                    border: '1px solid #4caf50',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#2e7d32',
                      fontSize: '12px',
                      fontWeight: 600,
                      mb: 1,
                      display: 'block',
                    }}
                  >
                    💡 Explication
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '13px',
                      color: '#1b5e20',
                      lineHeight: 1.6,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {latexToSimpleText(currentQuestion.explication)}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* Boutons de navigation */}
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          <Button
            variant="outlined"
            disabled={currentQuestionIndex === 0}
            onClick={handlePreviousQuestion}
            sx={{
              flex: 1,
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
            }}
          >
            Précédent
          </Button>
          <Button
            variant="contained"
            onClick={handleNextQuestion}
            disabled={currentQuestionIndex === totalQuestions - 1}
            sx={{
              flex: 1,
              bgcolor: '#4caf50',
              borderRadius: '20px',
              textTransform: 'none',
              fontWeight: 600,
              py: 1.5,
              '&:hover': {
                bgcolor: '#45a049',
              },
            }}
          >
            {currentQuestionIndex === totalQuestions - 1 ? 'Terminer' : 'Suivant'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ExerciceMobileView;
